// @ts-check
import { gql } from '@apollo/client';
const { findRoomByStandName } = require('./helpers/rooms');
const { getNumbers } = require('./helpers/stands');

/** @typedef {ReturnType<typeof getStands>[number]} Stand */
/**
 * Splits a section into multiple stands - either by splitting the stand number or by distributing the registered products.
 *
 * @template RegisteredProduct
 * @template {{ registration: { registeredProducts: { nodes: RegisteredProduct[] } } }} SectionData
 * @param {import('./@types/getStands').VieVinum_GetStandsFragment & SectionData} section
 * @param {(import('./@types/getStands').VieVinum_GetStandsFragment["registration"]["registeredProducts"]["nodes"][number] & RegisteredProduct)[]} registeredProducts
 */
export default function getStands(section, registeredProducts) {
  // const registeredProducts = section.registration.registeredProducts.nodes;
  const stands = parseStand(
    section.registration.stand?.name,
    registeredProducts
  );
  // Get the products that are statically assigned to a particular stand.
  const standProducts = stands.map((stand) =>
    registeredProducts.filter((product) => product.stand === stand.number)
  );

  // Then distribute the remaining products to the stands.
  // If there are more than 5 products, add them to the last stand.
  const remainingProducts = registeredProducts.filter(
    (product) => !standProducts.flat().includes(product)
  );
  remainingProducts.forEach((product) => {
    const stand =
      standProducts.find((stand) => stand.length < 5) ||
      standProducts[standProducts.length - 1];
    stand.push(product);
  });

  return stands.map((stand, index) => ({
    ...section,
    key: `${section.registration.registrationId}-${index}`,
    registration: {
      ...section.registration,
      stand,
    },
    registeredProducts: standProducts[index],
    stand,
    room: stand.room,
  }));
}

/**
 * @param {string?} standName
 * @param {any[]} products
 * @returns {{name: string; number: string; room?: import('./helpers/rooms').Room }[]}
 */
function parseStand(standName, products) {
  if (!standName) {
    // If there is no stand yet, derive the number of stands from the products entered.
    if (products.length > 5) {
      return [...Array(Math.ceil(products.length / 5))].map(() => ({
        name: standName,
        number: '',
      }));
    }
    return [{ name: standName, number: '' }];
  }

  const room = findRoomByStandName(standName);
  const tableNumbers = getNumbers(standName);
  return tableNumbers.map((number) => ({
    name: standName,
    number: customtableNumbers[number] ?? number,
    room,
  }));
}

const customtableNumbers = {
  // Südtirol has no number
  '245A': '',
  // Falstaff has no number
  552: '',
};

/** @type {import('@apollo/client').TypedDocumentNode<import('./@types/getStands').VieVinum_GetStandsFragment>}*/
getStands.fragment = gql`
  fragment VieVinum_getStands on RegistrationCatalogSection {
    registration {
      registrationId
      stand {
        name
      }
      registeredProducts {
        nodes {
          stand: customFieldText(key: "stand")
        }
      }
    }
  }
`;
